/**
 * Where a child goes when an enrichment class ends.
 *
 * Chess, tennis, piano and the maths blocks run for an hour inside the after-school day for
 * most of the children in them, and as a standalone class for the rest. Those two children
 * end the hour differently — one walks back to after-school, the other is collected by an
 * adult already waiting outside — and nothing on the row said which was which, so the
 * teacher had to hold it in their head. This term's chess rosters are 6-of-9, 6-of-8 and
 * 1-of-4: the mix is real and it is not guessable from a name.
 *
 * Both states are drawn, never just the positive one. A missing badge is read as "nothing
 * special about this child", which is the wrong reading for the child who is *not* going
 * back inside — they are the one somebody has to be waiting for. So the absence is stated
 * rather than implied.
 *
 * Only ever rendered on an enrichment roster. After-school and camp rosters would put the
 * same word on every row, and a Chinese or Math tab is made of after-school seats by
 * definition — see `isEnrichmentRoster`.
 */

interface Props {
  inAfterSchool: boolean;
}

export function AfterSchoolBadge({ inAfterSchool }: Props) {
  return inAfterSchool ? (
    <span
      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700"
      title="Stays on site — returns to BHCS After School when this class ends"
    >
      After School
    </span>
  ) : (
    <span
      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700"
      title="Not in BHCS After School — an adult collects this child when the class ends"
    >
      Pickup
    </span>
  );
}

/**
 * Whether this roster is one where the badge means anything.
 *
 * `mode` is `class` for every non-pickup program, which is the enrichment set plus anything
 * new that is not after-school or camp — the same fallback `attendanceModeFor` chooses, and
 * for the same reason: a new enrichment type behaves correctly the day it is created.
 *
 * The virtual exclusion is not reorderable. A Chinese or Math group's programs *are* the
 * after-school programs whose seats it borrows, so every child on it holds an after-school
 * enrollment — the badge would be the same word on every row, asserting a distinction that
 * cannot exist there.
 */
export function isEnrichmentRoster(mode: 'pickup' | 'class', isVirtual: boolean): boolean {
  return mode === 'class' && !isVirtual;
}
